import { omit } from 'lodash';
import { type ComponentPropsWithoutRef } from 'react';
import type { Components, ExtraProps } from 'react-markdown';

const HEADING_CLASSES = {
  h1: 'text-body-lg-heavy',
  h2: 'text-body-md-heavy',
  h3: 'text-body-sm-heavy',
  h4: 'text-body-sm-heavy',
  h5: 'text-body-xs-heavy',
  h6: 'text-body-xs-heavy',
} as const;

// NOTE(aman): `anywhere` also feeds min-content width, so leaving it on cells
// lets table columns collapse and split values mid-token.
const TABLE_CELL_CLASSES = 'px-2.5 py-1.5 [overflow-wrap:normal]';

export const MESSAGE_MARKDOWN_CLASSES = [
  'text-body-sm min-w-0 max-w-full [overflow-wrap:anywhere]',
  '[&_a]:text-brand-600 [&_a]:underline',
  '[&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:text-gray-500',
  '[&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs',
  '[&_li]:my-0.5',
  '[&_ol]:list-decimal [&_ol]:pl-5',
  '[&_ul]:list-disc [&_ul]:pl-5',
  '[&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
  '[&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-gray-100 [&_pre]:p-3',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_tbody_tr:last-child_td]:border-b-0',
].join(' ');

type HeadingTag = keyof typeof HEADING_CLASSES;
type MarkdownProps<Tag extends keyof JSX.IntrinsicElements> =
  ComponentPropsWithoutRef<Tag> & ExtraProps;

// NOTE(aman): react-markdown runs with `passNode: true`, so every renderer also
// receives the hast node, which React would warn about on a DOM element.
function withoutNode<Props extends ExtraProps>(
  props: Props
): Omit<Props, 'node'> {
  return omit(props, 'node');
}

function MarkdownLink(props: MarkdownProps<'a'>) {
  return (
    <a {...withoutNode(props)} target="_blank" rel="noopener noreferrer" />
  );
}

function MarkdownTable(props: MarkdownProps<'table'>) {
  return (
    <div className="my-2 overflow-x-auto">
      <table {...withoutNode(props)} className="text-body-xs w-full" />
    </div>
  );
}

function MarkdownTableHeaderCell(props: MarkdownProps<'th'>) {
  return (
    <th
      {...withoutNode(props)}
      scope="col"
      className={`${TABLE_CELL_CLASSES} whitespace-nowrap border-b border-gray-300 text-left font-semibold text-gray-700`}
    />
  );
}

function MarkdownTableCell(props: MarkdownProps<'td'>) {
  return (
    <td
      {...withoutNode(props)}
      className={`${TABLE_CELL_CLASSES} border-b border-gray-100 align-top`}
    />
  );
}

function createHeadingRenderer(tag: HeadingTag) {
  return function MarkdownHeading(props: MarkdownProps<'h1'>) {
    const Heading = tag;
    return (
      <Heading
        {...withoutNode(props)}
        className={`${HEADING_CLASSES[tag]} mb-1 mt-3 first:mt-0`}
      />
    );
  };
}

export const MARKDOWN_COMPONENTS: Components = {
  a: MarkdownLink,
  h1: createHeadingRenderer('h1'),
  h2: createHeadingRenderer('h2'),
  h3: createHeadingRenderer('h3'),
  h4: createHeadingRenderer('h4'),
  h5: createHeadingRenderer('h5'),
  h6: createHeadingRenderer('h6'),
  table: MarkdownTable,
  td: MarkdownTableCell,
  th: MarkdownTableHeaderCell,
};
